import { useState, useEffect, useRef } from 'react';
import { countriesDDI, type CountryDDI } from './countriesDDI';

export interface PhoneInputWithDDIProps {
  value: string;
  onChange: (fullPhone: string, ddi: string, localNumber: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  id?: string;
}

function CountryFlag({ country, className = 'h-5 w-7' }: { country: CountryDDI; className?: string }) {
  const [hasError, setHasError] = useState(false);
  const code = country.code.toLowerCase();
  const src = `https://flagcdn.com/w40/${code}.png`;

  if (hasError) {
    return (
      <span className="text-xl leading-none" aria-hidden>
        {country.flag}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={`Bandeira ${country.name}`}
      className={`${className} rounded-sm object-cover flex-shrink-0`}
      loading="lazy"
      onError={() => setHasError(true)}
    />
  );
}

function parseStoredPhone(stored: string): { ddi: string; local: string } {
  const digits = stored.replace(/\D/g, '');
  if (!digits) return { ddi: '+55', local: '' };
  const sortedByLen = [...countriesDDI].sort((a, b) => {
    const aLen = a.ddi.replace(/\D/g, '').length;
    const bLen = b.ddi.replace(/\D/g, '').length;
    return bLen - aLen;
  });
  for (const c of sortedByLen) {
    const ddiDigits = c.ddi.replace(/\D/g, '');
    if (ddiDigits && digits.startsWith(ddiDigits)) {
      return { ddi: c.ddi, local: digits.slice(ddiDigits.length) };
    }
  }
  if (digits.length >= 2 && digits.startsWith('55')) {
    return { ddi: '+55', local: digits.slice(2) };
  }
  if (digits.length <= 11 && /^\d+$/.test(digits)) {
    return { ddi: '+55', local: digits };
  }
  return { ddi: '+55', local: digits };
}

export function PhoneInputWithDDI({
  value,
  onChange,
  placeholder = '(11) 99999-9999',
  className = '',
  inputClassName = '',
  disabled = false,
  id = 'phone',
}: PhoneInputWithDDIProps) {
  const parsed = parseStoredPhone(value);
  const [selectedCountry, setSelectedCountry] = useState<CountryDDI>(() =>
    countriesDDI.find((c) => c.ddi === parsed.ddi) ?? countriesDDI[0]
  );
  const [localNumber, setLocalNumber] = useState(parsed.local);
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const p = parseStoredPhone(value);
    const country = countriesDDI.find((c) => c.ddi === p.ddi) ?? countriesDDI[0];
    setSelectedCountry(country);
    setLocalNumber(p.local);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCountries = countriesDDI.filter(
    (c) => c.name.toLowerCase().includes(search.toLowerCase()) || c.ddi.includes(search)
  );

  const notifyChange = (ddi: string, local: string) => {
    const ddiDigits = ddi.replace(/\D/g, '');
    const localDigits = local.replace(/\D/g, '');
    const full = ddiDigits && localDigits ? ddiDigits + localDigits : localDigits || '';
    onChange(full, ddi, local);
  };

  const handleCountrySelect = (c: CountryDDI) => {
    setSelectedCountry(c);
    setShowDropdown(false);
    setSearch('');
    notifyChange(c.ddi, localNumber);
  };

  const handleLocalChange = (v: string) => {
    setLocalNumber(v);
    notifyChange(selectedCountry.ddi, v);
  };

  return (
    <div ref={containerRef} className={`flex gap-2 items-stretch ${className}`}>
      <div className="relative flex-shrink-0" style={{ width: '130px' }}>
        <button
          type="button"
          onClick={() => !disabled && setShowDropdown(!showDropdown)}
          disabled={disabled}
          className="w-full h-full py-2.5 px-3 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CountryFlag country={selectedCountry} />
          <span className="text-sm font-medium">{selectedCountry.ddi}</span>
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showDropdown && (
          <div className="absolute top-full left-0 mt-1 w-80 bg-white rounded-xl shadow-xl z-50 max-h-72 overflow-hidden border border-gray-200">
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <input
                type="text"
                placeholder="Buscar país ou DDI..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
              />
            </div>
            <div className="overflow-y-auto max-h-56">
              {filteredCountries.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => handleCountrySelect(c)}
                  className="w-full px-4 py-2.5 hover:bg-orange-50 flex items-center gap-3 transition-colors border-b border-gray-100 last:border-b-0 text-left"
                >
                  <div className="w-8 flex justify-center">
                    <CountryFlag country={c} className="h-5 w-7" />
                  </div>
                  <span className="text-sm flex-1 font-medium text-gray-800">{c.name}</span>
                  <span className="text-sm font-semibold text-orange-600">{c.ddi}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <input
        type="tel"
        id={id}
        value={localNumber}
        onChange={(e) => handleLocalChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`flex-1 min-w-0 py-2.5 px-4 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-orange-400 ${inputClassName}`}
      />
    </div>
  );
}
